import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { GiLotusFlower } from 'react-icons/gi';
import { FaRupeeSign, FaStar, FaWhatsapp, FaInstagram, FaArrowLeft } from 'react-icons/fa';
import ReviewForm from '../components/ReviewForm';
import Loader from '../components/Loader';

const API = process.env.REACT_APP_API_URL;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProduct = async () => {
    try {
      const [pRes, rRes] = await Promise.all([
        axios.get(`${API}/api/products/${id}`),
        axios.get(`${API}/api/products/${id}/reviews`)
      ]);
      setProduct(pRes.data);
      setReviews(rRes.data);
    } catch {
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  if (loading) return <div style={{ paddingTop: '6rem' }}><Loader /></div>;
  if (!product) return null;

  const whatsappMsg = encodeURIComponent(`Hi! I want to order "${product.name}" - ₹${product.price}`);

  return (
    <div style={{ minHeight: '100vh', background: '#FDF6E3', paddingTop: '6rem' }}>

      {/* Back Button */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1.5rem 2rem 0' }}>
        <motion.button
          whileHover={{ x: -4 }} onClick={() => navigate('/products')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '8px',
            color: '#8B6914', fontFamily: 'Cinzel', fontSize: '0.85rem', letterSpacing: '2px'
          }}>
          <FaArrowLeft /> BACK TO PRODUCTS
        </motion.button>
      </div>

      {/* Product Section */}
      <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '3rem', alignItems: 'start'
        }}>

          {/* Image */}
          <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
            style={{
              borderRadius: '20px', overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(212,175,55,0.2)',
              border: '1px solid #e8c96d'
            }}>
            <img src={product.image} alt={product.name}
              style={{ width: '100%', height: '400px', objectFit: 'cover', display: 'block' }} />
          </motion.div>

          {/* Info */}
          <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
              <GiLotusFlower color="#D4AF37" size={16} />
              <span style={{ color: '#8B6914', fontFamily: 'Inter', fontSize: '0.8rem', letterSpacing: '2px' }}>
                {product.category}
              </span>
            </div>

            <h1 style={{
              fontFamily: 'Cinzel', fontSize: 'clamp(1.5rem, 4vw, 2.2rem)',
              color: '#2C1810', marginBottom: '1rem', lineHeight: 1.3
            }}>
              {product.name}
            </h1>

            {/* Rating */}
            {avgRating && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '1rem' }}>
                {[1,2,3,4,5].map(s => (
                  <FaStar key={s} size={16} color={s <= Math.round(avgRating) ? '#D4AF37' : '#ddd'} />
                ))}
                <span style={{ color: '#8B6914', fontFamily: 'Inter', fontSize: '0.85rem' }}>
                  {avgRating} ({reviews.length} reviews)
                </span>
              </div>
            )}

            {/* Price */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '1.5rem' }}>
              <FaRupeeSign color="#D4AF37" size={20} />
              <span style={{ fontFamily: 'Cinzel', fontSize: '2rem', color: '#D4AF37', fontWeight: '700' }}>
                {product.price}
              </span>
            </div>

            {/* Divider */}
            <div style={{ height: '1px', background: 'linear-gradient(to right, #D4AF37, transparent)', marginBottom: '1.5rem' }} />

            {/* Description */}
            <p style={{
              color: '#4a2f1a', fontFamily: 'Crimson Text',
              fontSize: '1.15rem', lineHeight: 1.8, marginBottom: '2rem'
            }}>
              {product.description}
            </p>

            {/* Stock */}
            <div style={{ marginBottom: '2rem' }}>
              <span style={{
                background: product.inStock ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                color: product.inStock ? '#15803d' : '#dc2626',
                padding: '0.4rem 1rem', borderRadius: '50px',
                fontFamily: 'Inter', fontSize: '0.8rem', fontWeight: '500'
              }}>
                {product.inStock ? '✓ In Stock' : '✗ Out of Stock'}
              </span>
            </div>

            {/* Order Buttons */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                href={`https://wa.me/+918700248228?text=${whatsappMsg}`}
                target="_blank" rel="noreferrer"
                style={{
                  background: '#25D366', color: '#fff',
                  padding: '0.9rem 2rem', borderRadius: '50px',
                  textDecoration: 'none', fontFamily: 'Cinzel',
                  fontSize: '0.85rem', letterSpacing: '2px',
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  boxShadow: '0 8px 25px rgba(37,211,102,0.3)'
                }}>
                <FaWhatsapp size={18} /> ORDER ON WHATSAPP
              </motion.a>

              <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                href="https://instagram.com/swarndhoop"
                target="_blank" rel="noreferrer"
                style={{
                  background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
                  color: '#fff', padding: '0.9rem 2rem', borderRadius: '50px',
                  textDecoration: 'none', fontFamily: 'Cinzel',
                  fontSize: '0.85rem', letterSpacing: '2px',
                  display: 'inline-flex', alignItems: 'center', gap: '8px'
                }}>
                <FaInstagram size={18} /> INSTAGRAM
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Reviews Section */}
      <section style={{
        maxWidth: '800px', margin: '0 auto',
        padding: '2rem 2rem 5rem'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <GiLotusFlower size={28} color="#D4AF37" />
          <h2 style={{ fontFamily: 'Cinzel', color: '#2C1810', letterSpacing: '4px', fontSize: '1.3rem', margin: '0.5rem 0' }}>
            CUSTOMER REVIEWS
          </h2>
          <div style={{ height: '1px', background: 'linear-gradient(to right, transparent, #D4AF37, transparent)' }} />
        </div>

        {/* Existing Reviews */}
        {reviews.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#8B6914', fontFamily: 'Crimson Text', fontSize: '1.1rem' }}>
            Be the first to share your experience 🙏
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
            {reviews.map((r, i) => (
              <motion.div key={r._id}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                style={{
                  background: 'linear-gradient(145deg, #fffdf5, #fdf0d0)',
                  border: '1px solid #e8c96d', borderRadius: '12px', padding: '1.5rem'
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontFamily: 'Cinzel', color: '#2C1810', fontSize: '0.95rem' }}>{r.name}</span>
                  <div style={{ display: 'flex', gap: '3px' }}>
                    {[1,2,3,4,5].map(s => (
                      <FaStar key={s} size={13} color={s <= r.rating ? '#D4AF37' : '#ddd'} />
                    ))}
                  </div>
                </div>
                <p style={{ color: '#4a2f1a', fontFamily: 'Crimson Text', fontSize: '1.05rem', lineHeight: 1.6 }}>
                  {r.comment}
                </p>
                <span style={{ color: '#8B6914', fontSize: '0.78rem', fontFamily: 'Inter' }}>
                  {new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </motion.div>
            ))}
          </div>
        )}

        {/* Review Form */}
        <ReviewForm productId={id} onReviewAdded={fetchProduct} />
      </section>
    </div>
  );
};

export default ProductDetail;